import { calculateMaximumInitialMarginFractionOverride } from '@katanaperps/katana-perps-sdk';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { setInitialMarginFractionOverride } from '#/api/apiExchange';
import {
  calculateInitialMarginFractionFromLeverageAndRoundUp,
  calculateLeverage,
} from '#/api/apiUtils';
import {
  useInitialMarginFractionOverrideQueries,
  useInvalidateInitialMarginFractionOverrideQueries,
} from '#/api/queries/initialMarginFractionOverrideQueries';
import { useMarketQuery } from '#/api/queries/marketsQueries';
import {
  useInvalidatePositionQueries,
  useWalletWithPositionsQuery,
} from '#/api/queries/walletPositionsQueries';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import SliderPercentage from '#/components/basic/inputs/Slider/SliderPercentage';
import ModalContent from '#/components/basic/modal/components/ModalContent';
import ModalFooterButtons from '#/components/basic/modal/components/ModalFooterButtons';
import SimpleModalOrMobilePage from '#/components/basic/modal/SimpleModalOrMobilePage';
import ErrorPanel from '#/components/basic/panels/ErrorPanel';
import MarginTypeChip from '#/components/markets/intermittent/MarginTypeChip';
import UnlockWalletButton from '#/components/wallet/UnlockWalletButton';
import { formatNumber } from '#/lib/display/formatting';
import { makeEthersErrorHumanRedable } from '#/lib/ethereum/ethersErrors';
import { signTypedData } from '#/lib/ethereum/sign/typedData/eip712';
import { sanitizeAndTruncateNumber } from '#/lib/numbers';
import { useSelector } from '#/reduxState/storeCore';

const Messages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  margin-top: 24px;
  text-align: center;
  font-size: var(--small);
  line-height: normal;
`;

const Warn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--yellow0);
`;

const IsolatedMarginNote = styled.div`
  color: var(--gray4c);
`;

const ConnectWalletWrap = styled.div`
  margin-top: 24px;
`;

const ValueBox = styled.div`
  display: flex;
  height: 40px;
  padding: 0px 8px;
  margin: 0 0 8px;
  justify-content: center;
  align-items: center;

  background: var(--black);
  border-radius: 2px;
  > span {
    padding-left: 4px;
    color: var(--gray4d);
  }
`;

/** https://docs.google.com/document/d/14YCM9ImOGKy5X9A3Vm01LOGAQf9L6PQTBsJOP1895ko */
export default function LeverageModal({
  market,
  initialMarginFractionOverride,
  onClose,
}: {
  market: string;
  initialMarginFractionOverride: string | null | undefined;
  onClose: () => void;
}) {
  const wallet = useSelector(state => state.wallet);
  const { data: marketData } = useMarketQuery(market);
  const invalidateQueries = useInvalidateInitialMarginFractionOverrideQueries();
  const invalidatePositionQueries = useInvalidatePositionQueries();
  const { data: walletWithPosition } = useWalletWithPositionsQuery();
  const { data: walletInitialMarginFractionOverridesData } =
    useInitialMarginFractionOverrideQueries({
      walletAddress: wallet.walletAddress,
      isVault: false,
    });
  // const { data: positionData } = usePositionQuery(market);
  const delegatedAuthorization = useSelector(
    state => state.delegatedKey.authorization
  );
  const walletAddress = wallet.walletAddress;

  const marketMaxLeverage = marketData?.initialMarginFraction
    ? sanitizeAndTruncateNumber(
        calculateLeverage(marketData.initialMarginFraction, false),
        0
      )
    : undefined;

  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [customLeverage, setCustomLeverage] = useState<number>();

  const { customIsUnderMinimum, minLeverageRounded } = useMemo(() => {
    if (
      !marketData ||
      !walletWithPosition ||
      !walletInitialMarginFractionOverridesData
    ) {
      return { customIsUnderMinimum: undefined, minLeverageRounded: undefined };
    }
    const maxIMFOverride = calculateMaximumInitialMarginFractionOverride(
      marketData,
      walletWithPosition,
      walletInitialMarginFractionOverridesData
    );
    const minLeverage = calculateLeverage(maxIMFOverride, false);
    const minLeverageRounded = Math.ceil(minLeverage);
    const customIsUnderMinimum = customLeverage
      ? customLeverage < minLeverageRounded
      : undefined;
    return {
      customIsUnderMinimum,
      minLeverageRounded,
    };
  }, [
    customLeverage,
    marketData,
    walletInitialMarginFractionOverridesData,
    walletWithPosition,
  ]);

  const persistedLeverage = initialMarginFractionOverride
    ? calculateLeverage(initialMarginFractionOverride)
    : false;

  const visibleLeverage =
    customLeverage || persistedLeverage || marketMaxLeverage;

  const handleConfirm = async () => {
    if (!customLeverage) {
      return;
    }
    setErrorMessage('');
    setProcessing(true);
    try {
      await setInitialMarginFractionOverride({
        market,
        walletAddress,
        delegatedKeyAddress: delegatedAuthorization.walletAddress,
        value:
          calculateInitialMarginFractionFromLeverageAndRoundUp(customLeverage),
        signer: (...params) =>
          signTypedData(
            wallet,
            delegatedAuthorization.walletAddress
              ? delegatedAuthorization
              : undefined,
            params
          ),
      });
      await Promise.all([invalidateQueries(), invalidatePositionQueries()]);
      onClose();
    } catch (error) {
      console.warn(error);
      setErrorMessage(makeEthersErrorHumanRedable(error));
      setProcessing(false);
    }
  };

  return (
    <SimpleModalOrMobilePage
      title={
        <>
          {market} Leverage
          {marketData?.marginType && (
            <MarginTypeChip
              marginType={marketData.marginType}
              marginLeft="8px"
            />
          )}
        </>
      }
      size="small"
      onClose={onClose}
      padding="0"
      verticalAlignOnMobile="centered"
    >
      {marketMaxLeverage ? (
        <div>
          <ModalContent>
            <ValueBox>
              {visibleLeverage}
              <span>x</span>
            </ValueBox>
            <SliderPercentage
              disabled={processing || !minLeverageRounded}
              onChange={value => setCustomLeverage(value)}
              min={1}
              max={Number(marketMaxLeverage)}
              value={Number(visibleLeverage || 0)}
              quickSelectIncludeMin
              quickSelectSign="x"
              hiddenLabel
            />
            <Messages>
              <Warn>
                <MaterialSymbol
                  icon="error"
                  fontSize="inherit"
                  color="var(--yellow0)"
                />
                Higher leverage increases the risk of liquidation
              </Warn>
              {marketData?.marginType === 'isolated' && (
                <IsolatedMarginNote>
                  This market uses <MarginTypeChip marginType="isolated" />{' '}
                  margin — only the margin allocated to this position is at
                  risk.
                </IsolatedMarginNote>
              )}
            </Messages>
            {customIsUnderMinimum && (
              <ErrorPanel
                marginTop={8}
                message={`Available collateral limits leverage to ${formatNumber(
                  minLeverageRounded,
                  { fixedDecimals: 0 }
                )}x`}
              />
            )}
            {errorMessage && (
              <ErrorPanel message={errorMessage} marginTop={8} />
            )}
            {!walletAddress && (
              <ConnectWalletWrap>
                <UnlockWalletButton onClick={onClose} />
              </ConnectWalletWrap>
            )}
          </ModalContent>
          {walletAddress && (
            <ModalFooterButtons
              confirmButtonProps={{
                onClick: handleConfirm,
                disabled:
                  !customLeverage ||
                  customLeverage === persistedLeverage ||
                  !marketData ||
                  processing ||
                  customIsUnderMinimum,
                children: 'Confirm',
              }}
            />
          )}
        </div>
      ) : (
        <div style={{ height: 200 }} />
      )}
    </SimpleModalOrMobilePage>
  );
}
